// Footer-improved.jsx - Modern Footer + Full Legacy Features (Promo, Role, Token, About, Contact, Session)
import React, { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';

import { FaMapMarkerAlt, FaSyncAlt } from 'react-icons/fa';

import {
  checkSession,
  refreshSession,
  getElakayLang,
  signout,
  isAuthenticated,
  signOutALL,
  SESSION_TIMEOUT_UNIT_MS,
  SESSION_WARNING_MS
} from '../auth';

import { laguangeElakay } from '../pageInside/dataElakay';
import SupportChatShell from '../pageInside/SupportChatShell';
import { Family, CommercialRole, getCurrentElakayRole } from '../pageInside/Rolle';
import { buildFooterNav } from './footerNav';
import FooterNavGroups from './FooterNavGroups';
import useEmailMarketingPermissions from '../pageInside/EmailMarketing/useEmailMarketingPermissions';
import RoleSwitchDropdown from '../../components/RoleSwitch/RoleSwitchDropdown';

import ShopUserView from '../pages/lakayUser/ShopUser';
import UserExitActAss from '../pages/userPageExiste/actPageAssUserExit';
import Pages from '../pages/LanguagePage';
import PageRenderer from '../pageInside/PageBuilder/PageRenderer';
import { useContentPageOverride } from '../pages/useContentPageOverride';
import { buildContentPageData, getContentPageBuilderDoc } from '../pages/contentPageUtils';

import nodata from '../Layout/imgs/nodata0.png';

import {
  useScrollAnimation,
  useFloatingParticles,
  useHoverAnimation,
  useRipple
} from './useHeaderAnimations';

import BankAccountRequiredModal from '../pageInside/BankAccountRequiredModal';
import FooterMobileApp from './FooterMobileApp';
import DraggableFloatingPanel from '../pageInside/DraggableFloatingPanel';
import { elakayAutoText } from "../pageInside/dataElakay";
import { chooseReadableTextColor } from '../../utils/colorContrast';
import { resolveUserAddingRoleToken, buildUserAddingRoleTokenPath } from '../../utils/userAddingRoleToken';
import './HeaderFooterCollections.css';
import { collectionAttribute, useHeaderFooterCollection } from './headerFooterCollections';


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["footer"]?.[key] || fallback;

// import './Footer.css';

/* ----------------------------- Helpers ----------------------------- */

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const pubFallback = {
  promoInfo: { _id: null, prod_id: null, customer_id: null },
  success: false,
  promoUrl: { urlProdView: null, pastProdView: null, urlShop: null, pastShop: null },
  error: __elakayText("t010", "Promo is not loading.")
};

const isTruthyPromo = (obj = { pubShow: false, pub: pubFallback }) => {
  if (obj?.pubShow && obj?.pub?.success && obj.pub?.promoInfo && typeof obj.pub.promoInfo === 'object') {
    const { prod_id, customer_id } = obj.pub.promoInfo;
    if (!prod_id && !customer_id) return false;
    return true;
  }
  return false;
};

const checkObjEmpty = (obj) => {
  if (Object.values(obj ? obj : [])?.length > 0) return true;
  return false;
};

// Position de depart du widget SessionExpiryWarning : bas a droite de
// l'ecran (l'utilisateur peut ensuite le glisser ailleurs via le
// DraggableFloatingPanel, voir pageInside/DraggableFloatingPanel.jsx).
const SESSION_WARNING_SIZE = { w: 285, h: 150 };
const getSessionWarningDefaultPos = () => {
  if (typeof window === "undefined") return { x: 24, y: 24 };
  const margin = 24;
  return {
    x: Math.max(margin, window.innerWidth - SESSION_WARNING_SIZE.w - margin),
    y: Math.max(margin, window.innerHeight - SESSION_WARNING_SIZE.h - margin),
  };
};

const renderNoData = () => (
  <div>
    <div className="row mt-4 mb-4 justify-content-center">
      <img className="shadow-lg" style={{ width: '100%' }} src={nodata} alt={__elakayText("t001", "no data")} />
    </div>
  </div>
);

const pageSteps = (a, options = {}) => {
  const { infoSource, pageKey = "aboutUs", pageTitle = "" } = options;
  const languageData = infoSource?.languageAPI?.elakayInfoAPI || infoSource?.languageAPI?.elakayInfo || null;
  const contentPage = buildContentPageData(pageKey, a, {
    label: pageTitle || pageKey,
    languageData,
    defaultRenderer: "LanguagePage",
  });
  const pageBuilderDoc =
    getContentPageBuilderDoc(contentPage) ||
    getContentPageBuilderDoc(a) ||
    getContentPageBuilderDoc(languageData?.contentPages?.[pageKey]) ||
    getContentPageBuilderDoc(languageData?.contentPageDocs?.[pageKey]);

  if (pageBuilderDoc) {
    return (
      <PageRenderer
        doc={pageBuilderDoc}
        pageData={contentPage}
        infoSource={infoSource}
        token={isAuthenticated()?.token}
        device="desktop"
      />
    );
  }

  if (contentPage?.sections?.length) {
    return (
      <Pages
        classAlert={'container-fluid'}
        classText={elakayAutoText("h_54e188944c1637", "col col-11")}
        classImg={elakayAutoText("h_34cf7f05bfb152", "col col-10")}
        classPara={elakayAutoText("h_71235f02727ebb", "row justify-content-center")}
        paragra0={contentPage.sections}
        contentPage={contentPage}
        infoSource={infoSource}
      />
    );
  }

  return renderNoData();
};

/* ----------------------------- Footer ----------------------------- */
const Footer = (props) => {
  const { 
    infoSource, 
    elakayCategories,

    // ── Animation controls (inchangés) ────────────────────────────────
    enableFooterAnimations    = false,
    enableSectionAnimations   = false,
    enableItemAnimations      = false,
    enableHoverAnimations     = false,
    enableButtonAnimations    = false,
    enableIconAnimations      = false,
    enableBackgroundGrid      = false,
    enableGradientOverlay     = false,
    enableShineEffect         = false,
    enableScrollAnimations    = false,
    enableStaggeredAnimations = false,
    enableFloatingParticles   = false,
    enableRippleEffect        = false,
    enableTitleAnimations     = false,
    enableLinkAnimations      = false,
    enableDividerAnimations   = false,
    enableCopyrightAnimations = false,
    footerGradientStart       = '#007399',
    footerGradientEnd         = '#005a73',

    // ── Couleurs Fond ─────────────────────────────────────────────────
    backgroundColor          = '#004b63',
    backgroundColorSecondary = '#005a73',
    backgroundColorTertiary  = '#003846',

    // ── Couleurs Texte ────────────────────────────────────────────────
    textColor          = '#ffffff',
    textColorSecondary = '#e2f3f7',

    // ── Couleurs Titres de sections ───────────────────────────────────
    titleColor       = '#ffffff',
    titleBorderColor = '#007399',

    // ── Couleurs Liens ────────────────────────────────────────────────
    linkColor                = '#f3fbff',
    linkHoverColor           = '#ffffff',
    linkHoverBackgroundColor = 'rgba(255,255,255,0.14)',

    // ── Couleurs Décoratif ────────────────────────────────────────────
    accentColor   = '#007399',
    dividerColor  = 'rgba(255,255,255,0.08)',
    particleColor = 'rgba(0,115,153,0.4)',
    gridColor     = 'rgba(255,255,255,0.03)',

    // ── Couleurs Copyright ────────────────────────────────────────────
    copyrightTextColor   = '#e2f3f7',
    copyrightBorderColor = 'rgba(255,255,255,0.06)',

    // ── Couleurs Boutons ──────────────────────────────────────────────
    buttonBackgroundColor = '#007399',
    buttonTextColor       = '#ffffff',
    buttonHoverColor      = '#00a4c8',

  } = props;
  __setElakayInfoSource(infoSource);

  const footerBackgrounds = [
    backgroundColor,
    backgroundColorSecondary,
    backgroundColorTertiary,
  ];
  const readableTextColor = chooseReadableTextColor(footerBackgrounds, textColor);
  const readableSecondaryTextColor = chooseReadableTextColor(footerBackgrounds, textColorSecondary);
  const readableTitleColor = chooseReadableTextColor(footerBackgrounds, titleColor);
  const readableLinkColor = chooseReadableTextColor(footerBackgrounds, linkColor);
  const readableLinkHoverColor = chooseReadableTextColor(footerBackgrounds, linkHoverColor);
  const readableCopyrightColor = chooseReadableTextColor(footerBackgrounds, copyrightTextColor);
  const readableButtonTextColor = chooseReadableTextColor(footerBackgrounds, buttonTextColor);

  // ════════════════════════════════════════════════════════════════════
  // CSS variables injectées sur <footer style={footerColorVars}>
  // C'est le SEUL changement structurel du fichier.
  // Le CSS (Footer.css / elakay_index.css) doit utiliser var(--ftr-xxx)
  // pour que les couleurs dynamiques s'appliquent à chaque élément.
  // Aucun sous-composant n'est modifié.
  // ════════════════════════════════════════════════════════════════════
  const footerColorVars = {
    '--ftr-bg'               : backgroundColor,
    '--ftr-bg-secondary'     : backgroundColorSecondary,
    '--ftr-bg-tertiary'      : backgroundColorTertiary,
    '--ftr-gradient'         : `linear-gradient(135deg, ${backgroundColor}, ${backgroundColorSecondary}, ${backgroundColorTertiary})`,
    '--ftr-text'             : readableTextColor,
    '--ftr-text-secondary'   : readableSecondaryTextColor,
    '--ftr-title'            : readableTitleColor,
    '--ftr-title-border'     : titleBorderColor,
    '--ftr-link'             : readableLinkColor,
    '--ftr-link-hover'       : readableLinkHoverColor,
    '--ftr-link-hover-bg'    : linkHoverBackgroundColor,
    '--ftr-accent'           : accentColor,
    '--ftr-divider'          : dividerColor,
    '--ftr-particle'         : particleColor,
    '--ftr-grid'             : gridColor,
    '--ftr-copyright-text'   : readableCopyrightColor,
    '--ftr-copyright-border' : copyrightBorderColor,
    '--ftr-btn-bg'           : buttonBackgroundColor,
    '--ftr-btn-text'         : readableButtonTextColor,
    '--ftr-btn-hover'        : buttonHoverColor,
    '--footer-gradient-start': footerGradientStart,
    '--footer-gradient-end'  : footerGradientEnd,
  };

  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();

  const footerRef = useRef(null);
  // Header & Footer Collections: no attribute at all for the ORIGINAL design.
  const hfCollection = useHeaderFooterCollection(infoSource);
  
  const createRippleOriginal = useRipple();
  const createRipple = enableRippleEffect ? createRippleOriginal : () => {};

  useFloatingParticles(footerRef, enableFloatingParticles ? 15 : 0);
  
  const [setSectionRefOriginal, isSectionVisibleOriginal] = useScrollAnimation(0.1);
  const setSectionRef = enableScrollAnimations ? setSectionRefOriginal : () => {};
  const isSectionVisible = enableScrollAnimations ? isSectionVisibleOriginal : true;

  const { userRole, getPromoInfo, setPromoInfo, userInfo } = infoSource || {};
  // resolveUserAddingRoleToken() rejects "undefined"/"null"/empty — no URL
  // param exists on this component, so this is purely the localStorage
  // fallback, but going through the shared helper keeps the validation rule
  // (and the "token_userAdding" key name) in one single place.
  const token_userAdding = useMemo(() => resolveUserAddingRoleToken(), []);
  const url_User_Shoosing = buildUserAddingRoleTokenPath(token_userAdding);
  const isNotHomePage = url_User_Shoosing ? location.pathname !== url_User_Shoosing : false;

  const aboutUsemail = query.get('aboutus');
  const auth = isAuthenticated();
  const token = auth?.token;

  const [loading, setLoading] = useState(false);
  const [showRoleExit, setRoleExit] = useState(true);
  const [showConditionsRole, setConditionsRole] = useState(false);
  const [hideBox, setHideBox] = useState(false);
  const [showUserAddingRole, setShowUserAddingRole] = useState(false);
  const [showContactUs, setShowContactUs] = useState(false);
  const [showAbout, setAbout] = useState(aboutUsemail ? aboutUsemail : false);
  const [showPromo] = useState(isTruthyPromo(getPromoInfo));

  // Avertissement de fin de session : des qu'il reste SESSION_WARNING_MS
  // (3 min) avant l'expiration reelle, on affiche ce petit widget flottant
  // en bas a droite au lieu de supprimer elakayUser directement, pour
  // laisser l'utilisateur annuler (prolonger la session) ou confirmer la
  // deconnexion. Son propre decompte (ci-dessous) est l'unique declencheur
  // de la deconnexion finale — checkSession ne rajoute pas de grace en plus.
  const SESSION_WARNING_SECONDS = SESSION_WARNING_MS / 1000;
  const [sessionWarningToken, setSessionWarningToken] = useState(null);
  const [sessionCountdown, setSessionCountdown] = useState(SESSION_WARNING_SECONDS);
  // Ref toujours synchrone avec sessionWarningToken — necesaire pour que
  // les handlers d'activite (definis dans un useEffect) voient la valeur
  // courante sans capturer une valeur perimee dans leur closure.
  const sessionWarningTokenRef = useRef(null);
  // Timestamp du dernier refresh d'activite pour throttler les ecritures
  // localStorage (on limite a une ecriture toutes les 30 s maximum).
  const lastActivityRefreshRef = useRef(0);

  const ilcod = infoSource?.languageAPI?.checkLangObjDefault;
  const ileia = infoSource?.languageAPI?.elakayInfoAPI;
  const ilei  = infoSource?.languageAPI?.elakayInfo;

  const InfoApiMenuList    = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'menuList')    : (ileia?.menuList    || {});
  const InfoApiTilte       = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'title')       : (ileia?.title       || {});
  const InfoApiText        = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'text')        : (ileia?.text        || {});
  const InfoApiBuy         = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'buy')         : (ileia?.buy         || {});
  const InfoApiButton      = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'Button')      : (ileia?.Button      || {});
  const InfoApiSell        = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'sell')        : (ileia?.sell        || {});
  const InfoApiHelpContact = !infoSource?.loading && ilcod ? ilcod(ileia, ilei, 'helpContact') : (ileia?.helpContact || {});

  // 4 columns (footerNav.js): My Account | My Activity | Explore Elakay | Elakay & Roles.
  const emailMarketing = useEmailMarketingPermissions({ enabled: CommercialRole() });
  const footerColumns = buildFooterNav(infoSource, {
    text: { menuList: InfoApiMenuList, title: InfoApiTilte, buy: InfoApiBuy, sell: InfoApiSell, helpContact: InfoApiHelpContact },
    categories: elakayCategories,
    emailMarketing: emailMarketing.access.canAccessEmailMarketing,
  });
  const footerActions = { about: () => setAbout(true), contact: () => setShowContactUs(true) };
  const footerItemProps = { enableItemAnimations, enableHoverAnimations, enableIconAnimations };
  // Any change of account or effective role (Switch Role) closes every footer menu.
  const footerRoleKey = `${auth?.a || 'visitor'}:${getCurrentElakayRole(infoSource) || ''}`;

  // Maintient le ref toujours synchro avec l'etat pour les closures.
  useEffect(() => {
    sessionWarningTokenRef.current = sessionWarningToken;
  }, [sessionWarningToken]);

  useEffect(() => {
    if (token_userAdding && isNotHomePage) {
      setShowUserAddingRole(true);
    }
    if (showRoleExit && checkObjEmpty(userRole?.userActiveRole)) {
      setConditionsRole(true);
    } else {
      setConditionsRole(false);
    }
    const sessionSettings = infoSource?.allSettings?.session;
    // Guard : ne remet pas le decompte a zero si le widget est deja affiche.
    // Sans cette garde, chaque tick de polling reprendrait le countdown
    // pendant que l'utilisateur regarde le widget se derouler.
    // remainingMs vient de checkSession : le temps reel restant avant
    // l'expiration, pour que le countdown affiche colle a la realite meme
    // si ce poll arrive un peu apres avoir franchi le seuil de 3 min.
    const warnSessionTimeout = (sessionToken, remainingMs) => {
      if (sessionWarningTokenRef.current) return;
      const seconds = Math.ceil((remainingMs ?? SESSION_WARNING_MS) / 1000);
      setSessionCountdown(Math.max(seconds, 0));
      setSessionWarningToken(sessionToken);
    };
    checkSession(sessionSettings, warnSessionTimeout);
    const amount = Number(sessionSettings?.timeoutAmount) || 30;
    const unit = SESSION_TIMEOUT_UNIT_MS[sessionSettings?.timeoutUnit] || SESSION_TIMEOUT_UNIT_MS.minutes;
    // Plafonne a 30s : avec un plafond plus large (ex. 5 min), le polling
    // peut sauter completement la fenetre d'avertissement de 3 min et
    // l'utilisateur se retrouve deconnecte sans avoir vu le widget.
    const pollInterval = Math.min(Math.max((amount * unit) / 10, 10 * 1000), 30 * 1000);
    const interval = setInterval(() => {
      checkSession(sessionSettings, warnSessionTimeout);
    }, pollInterval);
    return () => clearInterval(interval);
  }, [userRole, showConditionsRole, showRoleExit, token_userAdding, isNotHomePage, infoSource?.allSettings?.session]);

  // Pendant que le widget est affiche, decompte chaque seconde ; a 0,
  // deconnexion automatique.
  useEffect(() => {
    if (!sessionWarningToken) return;
    if (sessionCountdown <= 0) {
      signout(sessionWarningToken);
      setSessionWarningToken(null);
      return;
    }
    const timer = setTimeout(() => setSessionCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [sessionWarningToken, sessionCountdown]);

  // Rafraichit le timestamp de session sur toute activite utilisateur reelle
  // (clic, frappe clavier, scroll, touch). Throttle a 30 s max pour eviter
  // de surcharger localStorage. Si le widget d'expiration est deja affiche,
  // l'activite annule egalement l'avertissement (equivalent a "Cancel").
  useEffect(() => {
    const THROTTLE_MS = 30_000;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityRefreshRef.current < THROTTLE_MS) return;
      lastActivityRefreshRef.current = now;
      if (sessionWarningTokenRef.current) {
        // Activite pendant l'avertissement => annule la deconnexion.
        const stored = isAuthenticated();
        if (stored) {
          localStorage.setItem('elakayUser', JSON.stringify({ ...stored, t: now }));
        }
        setSessionWarningToken(null);
      } else {
        refreshSession();
      }
    };
    const events = ['click', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, handleActivity, { passive: true }));
  }, []); // enregistre une seule fois au montage ; la ref garantit la fraicheur

  // Rafraichit aussi le timestamp sur chaque navigation (changement de page).
  useEffect(() => {
    if (sessionWarningTokenRef.current) return; // navigation pendant l'avertissement => laisser le widget decider
    refreshSession();
  }, [location.pathname]);

  const cancelSessionLogout = () => {
    // Prolonge la session : rafraichit le timestamp et masque le widget.
    const stored = isAuthenticated();
    if (stored) {
      localStorage.setItem('elakayUser', JSON.stringify({ ...stored, t: new Date().getTime() }));
    }
    setSessionWarningToken(null);
  };

  const confirmSessionLogout = () => {
    signout(sessionWarningToken);
    setSessionWarningToken(null);
  };

  const btnShowPubPromo = () => {
    if (!setPromoInfo) return;
    setPromoInfo({ ...(getPromoInfo || {}), pubShow: false });
  };

  const directionPubPromo = (pastProdView) => {
    if (setPromoInfo) setPromoInfo({ ...(getPromoInfo || {}), pubShow: false });
    if (pastProdView) return navigate(pastProdView);
  };

  const messages = {
    fr: { title: __elakayText("t002", "Business Plan and Proposal"), text: __elakayText("t003", "Do you want to go to the Elakay Business Plan and Proposal page?"), button: 'Aller sur cette page', hideText: 'Cacher ce message', deleteToken: 'Supprimer ce jeton' },
    en: { title: __elakayText("t002", "Business Plan and Proposal"), text: __elakayText("t003", "Do you want to go to the Elakay Business Plan and Proposal page?"), button: 'Go in this page', hideText: 'Hide this message', deleteToken: 'Delete this token' },
    es: { title: __elakayText("t004", "Plan y Propuesta de Negocios"), text: __elakayText("t005", "¿Quieres ir a la página del Plan y Propuesta de Negocios de Elakay?"), button: 'Ir a esta página', hideText: 'Ocultar este mensaje', deleteToken: 'Eliminar este token' },
    ht: { title: __elakayText("t002", "Business Plan and Proposal"), text: __elakayText("t006", "Èske ou vle ale sou paj Plan ak Pwopozisyon Biznis Elakay la?"), button: 'Ale sou paj sa a', hideText: 'Kache mesaj sa a', deleteToken: 'Efase token sa a' },
    pt: { title: __elakayText("t007", "Plano e Proposta de Negócio"), text: __elakayText("t008", "Você quer acessar a página de Plano e Proposta de Negócio da Elakay?"), button: 'Ir para esta página', hideText: 'Ocultar esta mensagem', deleteToken: 'Excluir este token' }
  };

  const ElakayBusinessPlanPage = () => {
    const [selectedAction, setSelectedAction] = useState(null);
    const { obj } = getElakayLang();
    const code = obj?.code || 'en';
    const { title, text, button, hideText, deleteToken } = messages[code] || messages.en;

    const handleGo = () => {
      if (selectedAction === 'hide') {
        setHideBox(true);
        setShowUserAddingRole(false);
      } else if (selectedAction === 'delete') {
        setHideBox(false);
        setShowUserAddingRole(false);
        localStorage.removeItem('token_userAdding');
      } else {
        window.location.href = url_User_Shoosing;
      }
    };

    const handleRadioChange = (e) => {
      const value = e.target.value;
      if (selectedAction === value) setSelectedAction(null);
      else setSelectedAction(value);
    };

    if (hideBox) return null;

    return (
      <div style={{ padding: '20px', fontFamily: 'Arial' }}>
        <h2 className="text-info">🚀 {title}</h2>
        <p>{text}</p>
        <div className="form-group" style={{ marginTop: '20px' }}>
          <input
            className="form-check-input"
            type="checkbox"
            name="action"
            value="delete"
            onChange={handleRadioChange}
            checked={selectedAction === 'delete'}
            id="delete"
            style={{ width: '18px', height: '18px' }}
          />
          <label className="form-check-label" htmlFor="delete" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{deleteToken}</span>
          </label>
        </div>
        <button
          onClick={handleGo}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          {selectedAction ? elakayAutoText("h_3fa4054f214f0f", "submit") : button}
        </button>
      </div>
    );
  };

  if (hideBox) return null;

  return (
    <>
      {/* ════════════════════════════════════════════════════════════
          SEUL CHANGEMENT : style={footerColorVars} sur <footer>
          Tous les sous-composants et leur structure sont inchangés.
          ════════════════════════════════════════════════════════════ */}
      <footer
        className={`navFooter 
          ${!enableFooterAnimations ? 'no-footer-animations' : ''}
          ${!enableBackgroundGrid ? 'no-grid' : ''}
          ${!enableGradientOverlay ? 'no-overlay' : ''}
          ${!enableHoverAnimations ? 'no-hover' : ''}
          ${!enableIconAnimations ? 'no-icon-animations' : ''}
        `}
        // ref={footerRef}
        style={footerColorVars}
        data-hf-collection={collectionAttribute(hfCollection)}
      >
        <Card.Footer>
          <div className="container">
            <div className="footer-grid footer-grid-4">
              {footerColumns.map((column, index) => (
                <FooterSection
                  key={column.key}
                  title={column.title}
                  className={`footer-col-${column.key}`}
                  delay={enableSectionAnimations ? 100 + index * 100 : 0}
                  setSectionRef={setSectionRef}
                  isSectionVisible={isSectionVisible}
                  enableAnimations={enableSectionAnimations}
                  enableTitleAnimations={enableTitleAnimations}
                >
                  <FooterNavGroups
                    columnKey={column.key}
                    roleKey={footerRoleKey}
                    groups={column.groups}
                    Item={MenuItem}
                    itemProps={footerItemProps}
                    onAction={(name) => footerActions[name]?.()}
                    renderAuth={() => (
                      <AuthButtons
                        InfoApiButton={InfoApiButton}
                        token={token}
                        createRipple={createRipple}
                        enableButtonAnimations={enableButtonAnimations}
                        enableHoverAnimations={enableHoverAnimations}
                      />
                    )}
                    renderRoleSwitch={() => <RoleSwitchDropdown infoSource={infoSource} mode="list" />}
                  />
                </FooterSection>
              ))}
            </div>
          </div>
        </Card.Footer>

        <LocationControl infoSource={infoSource} />
        <FooterMobileApp />
        <CopyrightSection enableAnimations={enableCopyrightAnimations} />
      </footer>

      {/* Contact Modal */}
      <ContactModal
        show={showContactUs}
        onHide={() => setShowContactUs(false)}
        InfoApiTilte={InfoApiTilte}
        infoSource={infoSource}
      />

      {/* About Modal */}
      <AboutModal
        show={!!showAbout}
        onHide={() => setAbout(false)}
        InfoApiTilte={InfoApiTilte}
        infoSource={infoSource}
      />

      {/* UserAddingRoleByToken Modal */}
      <Modal
        show={showUserAddingRole && !hideBox}
        onHide={() => setShowUserAddingRole(false)}
        centered
      >
        <Modal.Body className="text-center">
          <ElakayBusinessPlanPage />
        </Modal.Body>
      </Modal>

      {/* Conditions Role Modal */}
      {userRole?.userActiveRole && (
        <UserExitActAss
          showProposition={showConditionsRole}
          setShowProposition={setRoleExit}
          laguangeElakay={laguangeElakay}
          infoSource={infoSource}
          userActiveRole={userRole}
          token={token}
          ActiveAgree={true}
        />
      )}

      {/* Promo Modal */}
      {isTruthyPromo(getPromoInfo) && (
        <div className="contaire">
          <div className="row justify-content-center">
            <Modal
              size="xl"
              show={isTruthyPromo(getPromoInfo)}
              onHide={btnShowPubPromo}
              dialogClassName="modal-90w"
              aria-labelledby="example-custom-modal-styling-title"
              centered
            >
              <Modal.Header closeButton />
              <Modal.Body>
                {isTruthyPromo(getPromoInfo) &&
                  getPromoInfo?.pub?.promoUrl?.pastProdView &&
                  directionPubPromo(getPromoInfo.pub.promoUrl.pastProdView)}
                {isTruthyPromo(getPromoInfo) &&
                  getPromoInfo?.pub?.promoInfo?.customer_id !== 'null' && (
                    <ShopUserView
                      setLoading={setLoading}
                      infoSource={infoSource}
                      showFilter={false}
                      userID={getPromoInfo.pub.promoInfo.customer_id}
                      myFilters={{ filters: { 'categoriesID.gategoriesLink': [] } }}
                    />
                  )}
              </Modal.Body>
            </Modal>
          </div>
        </div>
      )}
      <BankAccountRequiredModal userInfo={userInfo} infoSource={infoSource} />

      {sessionWarningToken && (
        <DraggableFloatingPanel
          title={__elakayText("t016", "Your session is about to expire")}
          isFloating={true}
          showMinimize={false}
          defaultPos={getSessionWarningDefaultPos()}
          defaultSize={SESSION_WARNING_SIZE}
          margins={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <SessionExpiryWarning
            secondsLeft={sessionCountdown}
            onCancel={cancelSessionLogout}
            onConfirm={confirmSessionLogout}
          />
        </DraggableFloatingPanel>
      )}
    </>
  );
};


const SessionExpiryWarning = ({ secondsLeft, onCancel, onConfirm }) => (
  <div className="session-expiry-warning" role="alertdialog" aria-live="assertive">
    <p className="session-expiry-warning__title">
      {__elakayText("t016", "Your session is about to expire")}
    </p>
    <p className="session-expiry-warning__message">
      {__elakayText("t017", "You will be signed out automatically in")}{' '}
      <span className="session-expiry-warning__countdown">{secondsLeft}s</span>
    </p>
    <div className="session-expiry-warning__actions">
      <button
        type="button"
        className="session-expiry-warning__btn session-expiry-warning__btn--cancel"
        onClick={onCancel}
      >
        {__elakayText("t018", "Cancel")}
      </button>
      <button
        type="button"
        className="session-expiry-warning__btn session-expiry-warning__btn--confirm"
        onClick={onConfirm}
      >
        {__elakayText("t019", "OK, sign me out")}
      </button>
    </div>
  </div>
);

/* ----------------------------- UI Components ----------------------------- */
/* Identiques à l'original — aucune modification */

const FooterSection = ({ 
  title, children, delay, setSectionRef, className = '',
  enableAnimations = false,
  enableTitleAnimations = false
}) => {
  const [isVisible, setIsVisible] = useState(!enableAnimations);

  useEffect(() => {
    if (!enableAnimations) return;
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay, enableAnimations]);

  const sectionStyle = enableAnimations ? {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(18px)',
    transition: 'all 0.6s ease-out'
  } : {};

  return (
    <div 
      className={`col footer-col ${className} ${!enableAnimations ? 'no-animation' : ''}`}
      style={sectionStyle} 
      // ref={setSectionRef}
    >
      <h5 className={`p-0 footer-title ${!enableTitleAnimations ? 'no-title-animation' : ''}`}>
        {title}
      </h5>
      {children}
    </div>
  );
};

const MenuItem = ({ 
  to, icon: Icon, children, delay = 0, onClick,
  enableItemAnimations = false,
  enableHoverAnimations = false,
  enableIconAnimations = false
}) => {
  const hoverAnimation = useHoverAnimation();
  const { isHovered, hoverProps, hoverStyle } = enableHoverAnimations 
    ? hoverAnimation 
    : { isHovered: false, hoverProps: {}, hoverStyle: {} };
  
  const [isVisible, setIsVisible] = useState(!enableItemAnimations);

  useEffect(() => {
    if (!enableItemAnimations) return;
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay, enableItemAnimations]);

  const itemStyle = enableItemAnimations ? {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateX(0)' : 'translateX(-14px)',
    transition: 'all 0.45s ease-out',
    ...hoverStyle
  } : hoverStyle;

  const iconStyle = enableIconAnimations && enableHoverAnimations ? {
    transform: isHovered ? 'scale(1.15) rotate(4deg)' : 'scale(1) rotate(0)',
    transition: 'all 0.25s ease'
  } : {};

  const className = `mt-0 mb-0 pt-1 pb-1 footer-item ${!enableItemAnimations ? 'no-item-animation' : ''} ${!enableHoverAnimations ? 'no-hover' : ''}`;

  if (onClick) {
    return (
      <h6 className={className} style={itemStyle} {...hoverProps}>
        {/* Opens a modal (About us, Contact Us): a real <a> so it keeps the footer
            link styles and keyboard access; `to` is only the no-JS / new-tab fallback. */}
        <a
          href={to}
          role="button"
          onClick={(event) => { event.preventDefault(); onClick(event); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {Icon && <Icon className="mr-2" style={iconStyle} />}
          <span className="footer-link-text">{children}</span>
        </a>
      </h6>
    );
  }

  return (
    <h6 className={className} style={itemStyle} {...hoverProps}>
      <Link to={to} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {Icon && <Icon className="mr-2" style={iconStyle} />}
        <span className="footer-link-text">{children}</span>
      </Link>
    </h6>
  );
};

const AuthButtons = ({ 
  InfoApiButton, token, createRipple,
  enableButtonAnimations = false,
  enableHoverAnimations = false
}) => {
  const hoverAnimation = useHoverAnimation();
  const { isHovered, hoverProps } = enableHoverAnimations 
    ? hoverAnimation 
    : { isHovered: false, hoverProps: {} };

  const buttonStyle = enableButtonAnimations && enableHoverAnimations ? {
    transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  } : {};

  const buttonClass = `btn btn-outline-light btn-md footer-btn ${!enableButtonAnimations ? 'no-button-animation' : ''} ${!enableHoverAnimations ? 'no-hover' : ''}`;

  if (!isAuthenticated()) {
    return (
      <Fragment>
        <Link className={buttonClass} to="/signin" style={buttonStyle} {...hoverProps} onClick={createRipple}>
          {InfoApiButton.signin}
        </Link>
        <Link className={buttonClass} to="/signup" style={buttonStyle} onClick={createRipple}>
          {InfoApiButton.signup}
        </Link>
      </Fragment>
    );
  }

  return (
    <div className="footer-auth-actions">
      <button className={buttonClass} style={buttonStyle}
        onClick={(e) => { createRipple(e); signout(token).then(() => (window.location.href = '/signin')); }}>
        {InfoApiButton.signOut}
      </button>
      <button className={buttonClass} style={buttonStyle}
        onClick={(e) => { createRipple(e); signOutALL(token).then(() => (window.location.href = '/signin')); }}>
        {InfoApiButton.signOutAll}
      </button>
    </div>
  );
};

const LocationControl = ({ infoSource }) => {
  const localizationEnabled = infoSource?.allSettings?.localization?.enabled ?? true;
  const currentLocation = infoSource?.location || {};
  const refreshLocation = infoSource?.getLocation;
  const [refreshError, setRefreshError] = useState(false);

  if (!localizationEnabled) return null;

  const locationLabel = [
    currentLocation.city,
    currentLocation.state,
    currentLocation.zipCode,
    currentLocation.country
  ].filter(Boolean).join(', ');

  const hasLocation = Boolean(locationLabel);
  const latitude = Number(currentLocation.latitude);
  const longitude = Number(currentLocation.longitude);
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);
  const locationHref = hasCoordinates
    ? `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=13/${latitude}/${longitude}`
    : hasLocation
    ? `https://www.openstreetmap.org/search?query=${encodeURIComponent(locationLabel)}`
    : "";
  const canOpenLocationMap = Boolean(locationHref) && !currentLocation.loading;
  const locationText = currentLocation.loading
    ? __elakayText("t011", "Checking location...")
    : hasLocation
    ? locationLabel
    : __elakayText("t013", "Location unavailable.");
  const canRefreshLocation = typeof refreshLocation === 'function' && !currentLocation.loading;

  const handleRefreshLocation = async () => {
    if (!canRefreshLocation) return;

    setRefreshError(false);
    try {
      await refreshLocation({ maximumAge: 0 });
    } catch (error) {
      setRefreshError(true);
    }
  };

  const handleOpenLocationMap = () => {
    if (!canOpenLocationMap || typeof window === "undefined") return;
    window.open(locationHref, "_blank", "noopener,noreferrer");
  };

  const handleLocationIconKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOpenLocationMap();
  };

  return (
    <div className="footer-location-control">
      <div className="footer-location-row" title={currentLocation.attribution || locationText}>
        <span
          className={`footer-location-map-icon ${canOpenLocationMap ? "active" : ""}`}
          role="button"
          tabIndex={canOpenLocationMap ? 0 : -1}
          aria-label={__elakayText("t015", "Open location map")}
          onClick={handleOpenLocationMap}
          onKeyDown={handleLocationIconKeyDown}
        >
          <FaMapMarkerAlt aria-hidden="true" />
        </span>
        <span className="footer-location-text">{locationText}</span>
        <Link
          type="button"
          className="footer-location-refresh"
          onClick={handleRefreshLocation}
          disabled={!canRefreshLocation}
          aria-label={__elakayText("t012", "Refresh location")}
          title={__elakayText("t012", "Refresh location")}
        >
          <FaSyncAlt aria-hidden="true" />
        </Link>
      </div>

      {(refreshError || currentLocation.error) && !currentLocation.loading && (
        <small className="footer-location-error" role="status">
          {currentLocation.error?.message || __elakayText("t013", "Location unavailable.")}
        </small>
      )}

      {Family() && hasLocation && (
        <a
          className="footer-location-attribution"
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          {__elakayText("t014", "© OpenStreetMap contributors")}
        </a>
      )}
    </div>
  );
};

const CopyrightSection = ({ enableAnimations = false }) => (
  <h6 className={`pt-2 pb-2 text-center footer-copyright ${!enableAnimations ? 'no-animation' : ''}`}>{__elakayText("t009", "Terms Conditions © 2018 -")}{new Date().getFullYear()} https://www.elakay.com
  </h6>
);

const ContactModal = ({ show, onHide, infoSource }) => (
  <Modal show={show} onHide={onHide} centered dialogClassName="contact-us-modal">
    <Modal.Body className="contact-us-modal-body">
      <SupportChatShell infoSource={infoSource} variant="widget" onClose={onHide} />
    </Modal.Body>
  </Modal>
);

// Only resolves the Family "System Templates" override while the modal is
// actually open (task spec §42: never fetch on every page load just because
// Footer -- and therefore this component -- mounts everywhere). Falls back
// to the exact same pageSteps() rendering as before whenever no override
// has Priority ON, so this can never regress the existing About Us page.
const AboutModal = ({ show, onHide, InfoApiTilte, infoSource }) => {
  // ARCHITECTURE CORRECTION: About Us's data source is fixed -- always
  // "aboutUs" -- never redirectable (a prior mission briefly let Family pick
  // a different data source here; that was explicitly retracted). Only the
  // DESIGN can vary, resolved entirely server-side before this hook sees a
  // response -- see useContentPageOverride's own header comment.
  const { doc: overrideDoc, htmlSource, htmlSourcePages } = useContentPageOverride(show ? "aboutUs" : null);
  const a = infoSource?.laguangeElakay?.('aboutUs', infoSource?.languageAPI);
  const pageTitle = InfoApiTilte?.aboutUs || InfoApiTilte?.aboutElakayTogether;

  return (
    <Modal size="xl" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h2>{InfoApiTilte.aboutElakayTogether}</h2>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {overrideDoc ? (
          <PageRenderer
            doc={overrideDoc}
            htmlSource={htmlSource}
            htmlSourcePages={htmlSourcePages}
            pageData={buildContentPageData("aboutUs", a, {
              label: pageTitle || "aboutUs",
              languageData: infoSource?.languageAPI?.elakayInfoAPI || infoSource?.languageAPI?.elakayInfo || null,
              defaultRenderer: "LanguagePage",
            })}
            infoSource={infoSource}
            token={isAuthenticated()?.token}
            device="desktop"
          />
        ) : (
          pageSteps(a, { infoSource, pageKey: "aboutUs", pageTitle })
        )}
      </Modal.Body>
    </Modal>
  );
};

export default Footer;
